/* Zeus product gallery rail (assets/zeus-gallery.css).
 *
 * Caps the thumbnail rail to the height of the main image, and adds the up/down
 * buttons that scroll it when there are more thumbnails than fit.
 *
 * The cap cannot be done in CSS. A flex container is as tall as its tallest
 * child, so align-items: stretch makes the rail stretch to its OWN height the
 * moment it is the tallest thing there - capping nothing. So the viewer is
 * measured and the rail gets an explicit max-height, kept in sync as the image
 * loads and the window resizes.
 *
 * Buttons rather than a scrollbar: the rail is 74px wide, where a scrollbar is
 * both ugly and an awkward target, and on macOS it is invisible until you are
 * already scrolling - so there would be no signal that more images exist.
 *
 * Dawn's own slider arrows are horizontal and are hidden in CSS; these are
 * separate controls, injected here so no Liquid change is needed.
 */
(() => {
  const DESKTOP = '(min-width: 990px)';

  const icon = (up) =>
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="' + (up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6') +
    '" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const setup = (rail) => {
    const list = rail.querySelector('.thumbnail-list');
    if (!list || rail.dataset.zeusRail) return;
    rail.dataset.zeusRail = '1';

    const gallery = rail.closest('media-gallery');
    const viewer = gallery && gallery.querySelector('[id^="GalleryViewer"]');

    const make = (up) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'zeus-railbtn zeus-railbtn--' + (up ? 'up' : 'down');
      b.setAttribute('aria-label', up ? 'Show previous images' : 'Show more images');
      b.innerHTML = icon(up);
      return b;
    };
    const up = make(true);
    const down = make(false);
    rail.insertBefore(up, list);
    rail.appendChild(down);

    // Scroll by a whole number of thumbnails so images never sit half-cut.
    const step = () => {
      const item = list.querySelector('.thumbnail-list__item');
      const h = item ? item.getBoundingClientRect().height + 8 : 82;
      return Math.max(h, Math.floor(list.clientHeight / h) * h - h);
    };

    // Two separate jobs, deliberately not merged.
    //
    // syncCap() measures and caps. It must clear the cap before reading, or a
    // measurement taken before the image loads locks the rail short forever -
    // but clearing it un-overflows the list, which momentarily resets scrollTop.
    // So this runs only when the geometry really changes: load, image load,
    // window resize.
    //
    // refresh() only reads scroll position to set button state. It runs on every
    // scroll, and must never touch the cap - doing both in one function made the
    // buttons read scrollTop as 0 mid-reflow and freeze in the wrong state.
    let capping = false;
    const syncCap = () => {
      if (!viewer || capping) return;
      capping = true;
      try {
        if (!window.matchMedia(DESKTOP).matches) { rail.style.maxHeight = ''; return; }
        const keep = list.scrollTop;
        rail.style.maxHeight = '';
        const h = Math.round(viewer.getBoundingClientRect().height);
        rail.style.maxHeight = h > 40 ? h + 'px' : '';
        list.scrollTop = keep;
      } finally {
        capping = false;
      }
      refresh();
    };

    const refresh = () => {
      const overflowing = list.scrollHeight - list.clientHeight > 2;
      const on = window.matchMedia(DESKTOP).matches && overflowing;
      up.hidden = !on;
      down.hidden = !on;
      up.disabled = list.scrollTop <= 1;
      down.disabled = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    };

    // Button state is refreshed from the click as well as from the scroll event.
    // The scroll event is the right hook and handles wheel and keyboard, but it
    // is not guaranteed to arrive - it never fires in the automation browser,
    // and a missed event leaves the arrows frozen pointing the wrong way. The
    // timeout matches the smooth-scroll settle.
    const nudge = (dir) => {
      list.scrollBy({ top: dir * step(), behavior: 'smooth' });
      setTimeout(refresh, 450);
    };
    up.addEventListener('click', () => nudge(-1));
    down.addEventListener('click', () => nudge(1));
    list.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', syncCap);

    // The cap depends on the main image's height, which is only known once it
    // has loaded - so re-measure when the gallery resizes, not just on load.
    // Only the viewer is observed. Observing the list too would wake this on
    // every cap change - the loop syncCap's guard exists to survive.
    if ('ResizeObserver' in window && viewer) new ResizeObserver(syncCap).observe(viewer);
    syncCap();
    // Images still loading change both the viewer height and scrollHeight.
    window.addEventListener('load', syncCap);
  };

  const init = () => document.querySelectorAll('media-gallery .thumbnail-slider').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
